import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getLivePicture } from '../services';

// Define the shape of user data
export interface UserData {
  userId: string;
  image: string;
  userRole: string;
  userName: string;
  userEmail: string;
}

interface UserContextType {
  user: UserData;
  fetchUserData: (practiseAdminId: string) => void;
  setUser: (arg: UserData) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserData>({
    userId: '',
    image: '',
    userRole: '',
    userName: '',
    userEmail: ''
  });

  const fetchUserData = async (practiseAdminId: string) => {
    try {
      const userRole = localStorage.getItem('userRole') || '';
      const userName = localStorage.getItem('userName') || '';
      const userEmail = localStorage.getItem('userEmail') || '';

      const response = await getLivePicture(practiseAdminId);
      const image = response?.practiseAdminLivePic?.[0]?.imageContent || '';

      setUser({
        userId: practiseAdminId,
        image,
        userRole,
        userName,
        userEmail
      });
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const userId = localStorage.getItem('userId');
      if (userId) {
        fetchUserData(userId);
      }
    }, 1000); // ⏱️ delay by 1000ms

    return () => clearTimeout(timer); // ✅ cleanup on unmount
  }, []);

  return (
    <UserContext.Provider value={{ user, fetchUserData, setUser }}>{children}</UserContext.Provider>
  );
};
